import { generateTimestampedId } from '../../helpers/unique-id.helper'
import { DwellClock } from './DwellClock'

export class ExposureTracker {
    constructor({
        placement,
        userId,
        submit,
        retention,
        newClock = () => new DwellClock(),
        newId = generateTimestampedId,
        now = () => new Date(),
    }) {
        this.placement = placement
        this.userId = userId
        this.submit = submit
        this.retention = retention
        this.newClock = newClock
        this.newId = newId
        this.now = now

        this.exposure = null
        this.clock = null
        this.paused = false
    }

    get current() {
        return this.exposure?.announcement ?? null
    }

    begin(announcement, trigger) {
        this.end()

        if (announcement == null) {
            return null
        }

        this.exposure = {
            id: this.newId(),
            announcement,
            trigger,
            startedAt: this.now().toISOString(),
        }

        this.clock = this.newClock()
        if (!this.paused) {
            this.clock.start()
        }

        this.refresh()

        return this.exposure
    }

    end() {
        if (this.exposure == null) {
            return Promise.resolve(null)
        }

        this.clock.stop()

        const impression = this.impressionFor(this.exposure)
        this.exposure = null
        this.clock = null

        return Promise.resolve(this.submit(impression))
            .then(() => this.retention.discard(impression.id))
            .catch((error) => {
                console.error('ExposureTracker: could not record an impression:', error)
            })
    }

    cancel() {
        if (this.exposure == null) {
            return
        }

        const { id } = this.exposure
        this.clock.stop()
        this.exposure = null
        this.clock = null

        this.retention.discard(id)
    }

    refresh() {
        if (this.exposure == null) {
            return
        }

        this.retention.retain(this.impressionFor(this.exposure))
    }

    pause() {
        this.paused = true
        this.clock?.pause()
        this.refresh()
    }

    resume() {
        this.paused = false
        this.clock?.resume()
    }

    impressionFor(exposure) {
        return {
            id: exposure.id,
            userId: this.userId,
            announcement: exposure.announcement.documentId,
            placement: this.placement,
            trigger: exposure.trigger,
            startedAt: exposure.startedAt,
            dwellDuration: this.clock?.elapsed ?? 0,
        }
    }
}
